import React, { useEffect, useRef, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { useFloatingAlert } from '@99designs/emu';
import { trackEvent } from '@99designs/tracking';
import { PrintTemplateQuery, usePrintTemplateLazyQuery } from './VistaprintProduct.generated';

export function usePrintTemplate(
    externalPrintId: number,
    serviceType: string,
    projectId?: string,
    location?: string
) {
    const downloadTemplateLinkRef = useRef<HTMLAnchorElement>(null);
    const floatingAlert = useFloatingAlert();
    const [templateBlobData, setTemplateBlobData] = useState('');

    const [getTemplate, { data, loading, error }] = usePrintTemplateLazyQuery({
        variables: {
            id: externalPrintId,
        },
        onError: () => {
            floatingAlert({
                message: __(
                    "Sorry, we're unable to download your print template right now. Please try again later."
                ),
                type: 'error',
            });
        },
    });

    // The second useEffect is needed, because the getTemplate query is very costly, we only want to call it once if there are
    // multiple Print template CTAs on the page.
    // Workflow:
    // 1. on print template CTA click: getTemplate (only call once, the second time it will read data from cache)
    // 2. xhr call to fetch response blob, and set template url
    // 3. once template url is set, call CTA click to trigger template downloading
    useEffect(() => {
        if (templateBlobData && downloadTemplateLinkRef.current) {
            downloadTemplateLinkRef.current.click();
        }
    }, [templateBlobData]);

    useEffect(() => {
        if (data) {
            downloadTemplate(data);
        }
    }, [data]);

    async function downloadTemplate(data: PrintTemplateQuery) {
        if (data.printById?.template) {
            const resp = await fetch(data.printById.template.url);
            const respBlob = await resp.blob();
            const url = window.URL.createObjectURL(respBlob);

            setTemplateBlobData(url);
        } else {
            Bugsnag.notify(new Error(`No print template URL for ${externalPrintId}`));
        }
    }

    function handlePrintTemplateOnClick(
        e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>
    ) {
        e.preventDefault();

        if (!data) {
            getTemplate();
        } else {
            downloadTemplate(data);
        }

        trackEvent('Print Template Downloaded', {
            project_id: projectId ? parseInt(projectId, 10) : 0,
            service_type: serviceType,
            view: location,
        });
    }

    return {
        downloadTemplateLinkRef,
        templateBlobData,
        filename: data?.printById.template?.filename,
        loading,
        error,
        handlePrintTemplateOnClick,
    };
}
